// Build identity belongs to the document, outside the JavaScript dependency
// graph. Looking for a newer build must never require auth or a database call.
let checkInFlight: Promise<string | null> | undefined;

export function currentBuildId(): string | null {
  if (typeof document === "undefined") return null;
  return document.querySelector<HTMLMetaElement>('meta[name="app-build-id"]')?.content?.trim() || null;
}

export function latestBuildId(): Promise<string | null> {
  if (checkInFlight) return checkInFlight;
  checkInFlight = Promise.resolve().then(async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2500);
    try {
      const response = await fetch("/app-version.json", {
        cache: "no-store", signal: controller.signal,
      });
      if (!response.ok || !response.headers.get("content-type")?.includes("application/json")) return null;
      const data = await response.json();
      return typeof data?.buildId === "string" && data.buildId.trim() ? data.buildId.trim() : null;
    } catch {
      return null;
    } finally {
      clearTimeout(timeout);
      checkInFlight = undefined;
    }
  });
  return checkInFlight;
}

export function isPageImportError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|not a valid JavaScript MIME type/i.test(message);
}

export async function pageUpdateAvailable(error: unknown): Promise<boolean> {
  if (!isPageImportError(error)) return false;
  const current = currentBuildId();
  if (current === null) return false;
  const latest = await latestBuildId();
  return latest !== null && latest !== current;
}
